<template>
    <div class="create-openrouter-form">
        <a-form>
            <div class="form-group">
                <label>{{ $t("whatsappChannel.channelName") }}</label>
                <a-input
                    v-model="form.channelName"
                    :placeholder="$t('whatsappChannel.channelName')"
                />
            </div>

            <div class="form-group">
                <label>{{ $t("message.apiKeyMode") || "API Anahtarı Modu" }}</label>
                <a-radio-group v-model="form.keyMode" class="key-mode-group">
                    <a-radio value="byok" class="key-mode-option">
                        <div class="key-mode-content">
                            <span class="key-mode-title">{{ $t("message.byokTitle") || "Kendi API Anahtarımı Kullanacağım (BYOK)" }}</span>
                            <span class="key-mode-desc">{{ $t("message.byokDesc") || "OpenRouter hesabınızdaki API anahtarınızı kullanın" }}</span>
                        </div>
                    </a-radio>
                    <a-radio value="supsis" disabled class="key-mode-option disabled">
                        <div class="key-mode-content">
                            <span class="key-mode-title">
                                {{ $t("message.supsisKeyTitle") || "Supsis AI API Anahtarı Sağlasın" }}
                                <a-tag color="orange">{{ $t("chatbotv2.comingSoon") || "Yakında" }}</a-tag>
                            </span>
                            <span class="key-mode-desc">{{ $t("message.supsisKeyDesc") || "Supsis tarafından sağlanan API anahtarı ile kullanın" }}</span>
                        </div>
                    </a-radio>
                </a-radio-group>
            </div>

            <div class="form-group" v-if="form.keyMode === 'byok'">
                <label>{{ $t("message.openrouterApiKey") || "OpenRouter API Key" }}</label>
                <a-input-password
                    v-model="form.apiKey"
                    :placeholder="$t('message.openrouterApiKey')"
                />
                <div class="help-text">
                    <a-icon type="info-circle" />
                    <span>
                        {{ $t("message.openrouterApiKeyHint") || "API anahtarınızı" }}
                        <a href="https://openrouter.ai/keys" target="_blank">OpenRouter Dashboard</a>
                        {{ "üzerinden alabilirsiniz." }}
                    </span>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <a-button @click="handleCancel">{{ $t("message.cancel") }}</a-button>
            <a-button type="primary" @click="handleSave" :loading="loading">
                {{ $t("message.save") }}
            </a-button>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

const PROVIDER_NAMES = {
    openrouter: "OpenRouter",
    openai: "OpenAI",
    google: "Google AI",
    anthropic: "Anthropic",
    mistral: "Mistral",
    azure: "Azure",
    bedrock: "Bedrock",
    cohere: "Cohere",
    xai: "xAI",
    deepinfra: "DeepInfra",
    together: "Together",
    fireworks: "Fireworks",
    siliconflow: "SiliconFlow",
    parasail: "Parasail",
    nebius: "Nebius",
    alibaba: "Alibaba Cloud",
    xiaomi: "Xiaomi",
    sambanova: "SambaNova",
    moonshot: "Moonshot AI",
    minimax: "Minimax",
    nvidia: "NVIDIA",
};

export default {
    name: "CreateOpenRouterChannelForm",
    props: {
        provider: {
            type: String,
            default: "openrouter",
        },
    },
    data() {
        const providerName = PROVIDER_NAMES[this.provider] || this.provider;
        return {
            form: {
                channelName: `LLM - ${providerName}`,
                keyMode: "byok",
                apiKey: "",
            },
            loading: false,
        };
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
        }),
        handleCancel() {
            this.$emit("cancel");
        },
        async handleSave() {
            if (!this.form.channelName) {
                this.$message.error(this.$t("message.requiredChannel") || "Kanal adı zorunludur");
                return;
            }

            if (this.form.keyMode === "byok" && !this.form.apiKey) {
                this.$message.error(this.$t("message.apiKeyRequired") || "API anahtarı zorunludur");
                return;
            }

            this.loading = true;
            try {
                const payload = {
                    type: "openrouter",
                    name: this.form.channelName,
                    payload: {
                        provider: this.provider,
                        keyMode: this.form.keyMode,
                        apiKey: this.form.apiKey,
                    },
                };

                await this.createChannel(payload);
                this.$message.success(this.$t("message.channelCreatedSuccessfully") || "Kanal başarıyla oluşturuldu");
                this.$emit("ok");
            } catch (err) {
                this.$message.error(this.$t("message.failedToSaveChannel") || "Kanal kaydedilemedi");
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.create-openrouter-form {
    .form-group {
        margin-bottom: 20px;

        label {
            display: block;
            font-weight: 500;
            margin-bottom: 8px;
        }

        .help-text {
            margin-top: 8px;
            font-size: 12px;
            color: var(--color-text-muted);
            display: flex;
            align-items: center;
            gap: 5px;

            a {
                color: var(--color-primary);
            }
        }
    }

    .key-mode-group {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .key-mode-option {
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
            padding: 12px 15px;
            margin: 0;
            width: 100%;

            &:hover {
                border-color: var(--color-primary);
            }

            &.disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }

            .key-mode-content {
                display: flex;
                flex-direction: column;
                gap: 4px;

                .key-mode-title {
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .key-mode-desc {
                    font-size: 12px;
                    color: var(--color-text-muted);
                }
            }
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid var(--color-border-default);
    }
}
</style>
